<template>
  <RfDialog
    ref="dialog"
    :title="title"
    width="600"
    confirmText="Save"
    cancelText="Close"
    keepOpenOnConfirm
    :disabled="confirmDisabledMessage"
    @confirm="handleConfirm"
    @open="syncFromModel"
  >
    <div class="flex flex-col gap-5">
      <RfAlert v-if="warningText" type="warning">{{ warningText }}</RfAlert>

      <div v-if="!isPushNotification" class="flex flex-col gap-5">
        <div class="flex flex-col gap-3">
          <RfCheckbox :modelValue="is_limited" @update:modelValue="toggleLimited">
            Impression limit
          </RfCheckbox>
          <div
            v-if="is_limited"
            class="border-beige-10 flex flex-col gap-3 rounded-lg border border-solid p-4"
          >
            <div class="flex items-start gap-3">
              <span class="text-body text-beige-90 w-44 shrink-0 pt-2.5">
                Max number of impressions:
              </span>
              <RfInput
                v-model="limit"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
                :hintMessage="currLimit || 'Specify the maximum allowed impressions.'"
                :errorMessage="fieldErrors.limit"
              />
            </div>
            <div class="flex items-start gap-3">
              <span class="text-body text-beige-90 w-44 shrink-0 pt-2.5">Days to reset:</span>
              <RfSelect
                v-model="trafficResetDays"
                variant="outlined"
                :items="resetDaysItems"
                placeholder="Select days"
                :disabled="!is_limited"
                hintMessage="Specify the number of days before the limit resets. Leave blank for no reset."
                clearable
                autoWidth
                class="w-full"
              />
            </div>
            <div v-if="model.id && is_limited" class="flex items-center gap-3">
              <div class="bg-beige-5 h-2.5 flex-1 overflow-hidden rounded-full">
                <div
                  class="h-full rounded-full bg-blue-50"
                  :style="{ width: `${currLimitPercent}%` }"
                ></div>
              </div>
              <strong class="text-body">{{ currLimitPercent }}%</strong>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-3">
          <RfCheckbox v-model="is_capped">Frequency limit</RfCheckbox>
          <div
            v-if="is_capped"
            class="border-beige-10 flex flex-col gap-3 rounded-lg border border-solid p-4"
          >
            <div class="flex items-start gap-3">
              <span class="text-body text-beige-90 w-44 shrink-0 pt-2.5">Type:</span>
              <RfSelect
                :modelValue="frequencyCapType"
                variant="outlined"
                :items="[VISIT, DAYS]"
                :disabled="!is_capped"
                hintMessage="Select the type of limit"
                autoWidth
                class="w-full"
                @update:modelValue="setFrequencyCapType"
              />
            </div>
            <div class="flex items-start gap-3">
              <span class="text-body text-beige-90 w-44 shrink-0 pt-2.5">Number of times:</span>
              <RfInput
                v-model="cap"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
                :hintMessage="freqCapHint"
                :errorMessage="fieldErrors.cap"
                :disabled="!is_capped"
              />
            </div>
            <div v-if="frequencyCapType === DAYS.value" class="flex items-start gap-3">
              <span class="text-body text-beige-90 w-44 shrink-0 pt-2.5">Days to reset:</span>
              <RfSelect
                v-model="cap_seconds"
                variant="outlined"
                :items="capSecondsInDaysItems"
                :disabled="!is_capped"
                hintMessage="Specify the number of days to reset the limit."
                autoWidth
                class="w-full"
              />
            </div>
          </div>
        </div>

        <div v-if="!isSequence" class="flex flex-col gap-3">
          <RfCheckbox :modelValue="is_budgeted" @update:modelValue="toggleBudgeted">
            Set a budget
          </RfCheckbox>
          <div
            v-if="is_budgeted"
            class="border-beige-10 flex flex-col gap-3 rounded-lg border border-solid p-4"
          >
            <div class="flex items-start gap-3">
              <span class="text-body text-beige-90 w-44 shrink-0 pt-2.5">Goal occurrences</span>
              <RfInput
                v-model="budget"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
                :hintMessage="
                  currBudget || 'Specify the max number of goal occurrences for this prompt'
                "
                :errorMessage="fieldErrors.budget"
              />
            </div>
            <div class="flex items-start gap-3">
              <span class="text-body text-beige-90 w-44 shrink-0 pt-2.5">Deduction per goal:</span>
              <RfInput
                v-model="budget_decrementer"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
                hintMessage="Specify the amount to deduct each successful goal."
                :errorMessage="fieldErrors.budget_decrementer"
              />
            </div>
            <div v-if="model.id && is_budgeted" class="flex items-center gap-3">
              <div class="bg-beige-5 h-2.5 flex-1 overflow-hidden rounded-full">
                <div
                  class="h-full rounded-full bg-blue-50"
                  :style="{ width: `${currBudgetPercent}%` }"
                ></div>
              </div>
              <strong class="text-body">{{ currBudgetPercent }}%</strong>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-3">
          <RfCheckbox :modelValue="hasUserLimit" @update:modelValue="toggleUserLimit">
            User limit
          </RfCheckbox>
          <div
            v-if="hasUserLimit"
            class="border-beige-10 flex flex-col gap-3 rounded-lg border border-solid p-4"
          >
            <div class="flex items-start gap-3">
              <span class="text-body text-beige-90 w-44 shrink-0 pt-2.5">Max number of users:</span>
              <RfInput
                v-model="userLimit"
                type="number"
                variant="outlined"
                class="w-full"
                autoWidth
                :hintMessage="userLimitText || 'Specify the maximum allowed users.'"
                :errorMessage="fieldErrors.userLimit"
              />
            </div>
            <div class="flex items-start gap-3">
              <span class="text-body text-beige-90 w-44 shrink-0 pt-2.5">Days to reset:</span>
              <RfSelect
                v-model="userResetDays"
                variant="outlined"
                :items="resetDaysItems"
                placeholder="Select days"
                :disabled="!hasUserLimit"
                hintMessage="Specify the number of days before the limit resets. Leave blank for no reset."
                clearable
                autoWidth
                class="w-full"
              />
            </div>
            <div v-if="model.id && hasUserLimit" class="flex items-center gap-3">
              <div class="bg-beige-5 h-2.5 flex-1 overflow-hidden rounded-full">
                <div
                  class="h-full rounded-full bg-blue-50"
                  :style="{ width: `${userLimitPercent}%` }"
                ></div>
              </div>
              <strong class="text-body">{{ userLimitPercent }}%</strong>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-3">
          <RfCheckbox :modelValue="hasDeliveryLimit" @update:modelValue="toggleDeliveryLimit">
            Delivery limit
          </RfCheckbox>
          <div
            v-if="hasDeliveryLimit"
            class="border-beige-10 flex flex-col gap-3 rounded-lg border border-solid p-4"
          >
            <div class="flex items-start gap-3">
              <span class="text-body text-beige-90 w-44 shrink-0 pt-2.5">
                Max number of deliveries:
              </span>
              <RfInput
                v-model="deliveryLimit"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
                :hintMessage="deliveryLimitText || 'Specify the maximum allowed unique deliveries.'"
                :errorMessage="fieldErrors.deliveryLimit"
              />
            </div>
            <div class="flex items-start gap-3">
              <span class="text-body text-beige-90 w-44 shrink-0 pt-2.5">Days to reset:</span>
              <RfSelect
                v-model="deliveryResetDays"
                variant="outlined"
                :items="resetDaysItems"
                placeholder="Select days"
                :disabled="!hasDeliveryLimit"
                hintMessage="Specify the number of days before the limit resets. Leave blank for no reset."
                clearable
                autoWidth
                class="w-full"
              />
            </div>
            <div v-if="model.id && hasDeliveryLimit" class="flex items-center gap-3">
              <div class="bg-beige-5 h-2.5 flex-1 overflow-hidden rounded-full">
                <div
                  class="h-full rounded-full bg-blue-50"
                  :style="{ width: `${deliveryLimitPercent}%` }"
                ></div>
              </div>
              <strong class="text-body">{{ deliveryLimitPercent }}%</strong>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isPushNotification" class="flex flex-col gap-5">
        <div class="flex flex-col gap-4">
          <div class="text-base">Only send to user if user has received:</div>
          <div class="flex flex-col gap-3">
            <div class="flex items-center gap-2">
              <span class="text-body w-40 font-semibold">From this prompt:</span>
              <span>&lt;</span>
              <RfInput
                v-model="sendLimits.user_prompt.max"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
              />
              <span class="text-body">in the last</span>
              <RfInput
                v-model="sendLimits.user_prompt.days"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
              />
              <span class="text-body">days</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-body w-40 font-semibold">From all prompts:</span>
              <span>&lt;</span>
              <RfInput
                v-model="sendLimits.user_app.max"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
              />
              <span class="text-body">in the last</span>
              <RfInput
                v-model="sendLimits.user_app.days"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
              />
              <span class="text-body">days</span>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-4">
          <div class="text-base">Only send prompt if total sends:</div>
          <div class="flex flex-col gap-3">
            <div class="flex items-center gap-2">
              <span class="text-body w-40 font-semibold">By this prompt:</span>
              <span>&lt;</span>
              <RfInput
                v-model="sendLimits.total_prompt.max"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
              />
              <span class="text-body">in the last</span>
              <RfInput
                v-model="sendLimits.total_prompt.days"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
              />
              <span class="text-body">days</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-body w-40 font-semibold">By all prompts:</span>
              <span>&lt;</span>
              <RfInput
                v-model="sendLimits.total_app.max"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
              />
              <span class="text-body">in the last</span>
              <RfInput
                v-model="sendLimits.total_app.days"
                type="number"
                variant="outlined"
                autoWidth
                class="w-full"
              />
              <span class="text-body">days</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </RfDialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/yup";
import { mixed, number, object } from "yup";
import { DAYS, VISIT } from "@/utils/constants/FrequencyCapConstants";
import { GUIDE_TYPES } from "@/utils/constants/GuidesConstants";
import { useAppsStore } from "@/pinia/appsStore";
import { isPushNotification as isPushNotificationHelper } from "@/utils/prompts/promptHelpers";
import RfAlert from "@/components/alerts/RfAlert.vue";
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import { cloneDeep } from "lodash-es";

defineOptions({ name: "RfTraffic" });

const props = defineProps({
  model: { type: Object, required: true },
  title: { type: String, default: "Limit this prompt:" },
});
const emit = defineEmits(["confirm"]);

const appsStore = useAppsStore();
const currApp = computed(() => appsStore.app);

const dialog = ref();

const is_limited = ref(false);
const trafficResetDays = ref(null);
const is_budgeted = ref(false);
const is_capped = ref(false);
const cap_seconds = ref(null);
const hasUserLimit = ref(false);
const userResetDays = ref(null);
const hasDeliveryLimit = ref(false);
const deliveryResetDays = ref(null);
const frequencyCapType = ref(null);
const sendLimits = ref({
  user_prompt: { days: null, max: null },
  user_app: { days: null, max: null },
  total_prompt: { days: null, max: null },
  total_app: { days: null, max: null },
});

const isSequence = computed(() => !!props.model?.sequence_type);
const isPushNotification = computed(() => isPushNotificationHelper(props.model));
const isWebClientSequence = computed(() => props.model?.sequence_type === "web_client");

const capSecondsInDaysItems = computed(() => {
  const maxDays = currApp.value?.flags?.extended_interactions_interval ? 365 : 30;
  return new Array(maxDays)
    .fill()
    .map((_, index) => ({ text: `${index + 1}`, value: (index + 1) * 60 * 60 * 24 }));
});

const resetDaysItems = computed(() => {
  const maxDays = currApp.value?.flags?.extended_interactions_interval ? 365 : 30;
  return new Array(maxDays).fill().map((_, index) => ({ text: `${index + 1}`, value: index + 1 }));
});

const currBudget = computed(() => {
  if (!is_budgeted.value) return null;
  return `(Remaining Available: ${budget.value - (props.model?.curr_budget || 0)})`;
});

const currBudgetPercent = computed(() =>
  calculatePercent(parseInt(budget.value), parseInt(props.model?.curr_budget)),
);

const currLimit = computed(() => {
  if (!is_limited.value) return null;
  return `(Remaining Available: ${limit.value - (props.model?.curr_limit || 0)})`;
});

const currLimitPercent = computed(() =>
  calculatePercent(parseInt(limit.value), parseInt(props.model?.curr_limit)),
);

const freqCapHint = computed(() => {
  if (props.model?.subject_name === "Sequence" && frequencyCapType.value === VISIT.value) {
    return "Specify the max number of times a user can be shown all prompts in this guide per visit.";
  }
  if (props.model?.subject_name === "Sequence") {
    return "Specify the max number of times a user can be shown all prompts in this guide.";
  }
  if (frequencyCapType.value === VISIT.value) {
    return "Specify the max number of times a user can be shown this prompt per visit.";
  }
  return "Specify the max number of times a user can be shown this prompt.";
});

const userLimitText = computed(() => {
  if (!hasUserLimit.value) return null;
  return `(Remaining Available: ${userLimit.value - (props.model?.curr_users || 0)})`;
});

const deliveryLimitText = computed(() => {
  if (!hasDeliveryLimit.value) return null;
  return `(Remaining Available: ${deliveryLimit.value - (props.model?.curr_deliveries || 0)})`;
});

const userLimitPercent = computed(() =>
  calculatePercent(parseInt(userLimit.value), parseInt(props.model?.curr_users)),
);

const deliveryLimitPercent = computed(() =>
  calculatePercent(parseInt(deliveryLimit.value), parseInt(props.model?.curr_deliveries)),
);

const warningText = computed(() => {
  if (isPushNotification.value) return null;
  let text = "Modifying limits after a prompt or guide is started may impact performance.";
  if (isWebClientSequence.value)
    text += ` For ${GUIDE_TYPES.web_client.label} guides, limits are enforced for the first prompt only.`;
  return text;
});

const positiveNumber = () =>
  number()
    .typeError("Must be a valid number")
    .required("Must be a valid number")
    .positive("Must be greater than 0");

// Only fields of an enabled section are validated — the legacy v-form skipped
// validation for disabled inputs.
const validationSchema = computed(() =>
  toTypedSchema(
    object({
      limit: is_limited.value ? positiveNumber() : mixed().notRequired(),
      cap: is_capped.value ? positiveNumber() : mixed().notRequired(),
      budget: is_budgeted.value ? positiveNumber() : mixed().notRequired(),
      budget_decrementer: is_budgeted.value ? positiveNumber() : mixed().notRequired(),
      userLimit: hasUserLimit.value ? positiveNumber() : mixed().notRequired(),
      deliveryLimit: hasDeliveryLimit.value ? positiveNumber() : mixed().notRequired(),
    }),
  ),
);

const { defineField, resetForm, validate, values } = useForm({
  validationSchema,
  initialValues: {},
});

// vee-validate's typed-schema validate() returns the result without committing
// it to field state, so the chain below applies it to an explicit snapshot:
// queued validations run against the latest values and the last one wins.
const validationState = ref({ valid: true, errors: {} });

// String disables the confirm button and renders as its tooltip (RfButtonNew).
const confirmDisabledMessage = computed(() =>
  !validationState.value.valid ? "Fix validation errors before saving" : false,
);

const fieldErrors = computed(() => validationState.value.errors);

const [limit] = defineField("limit");
const [cap] = defineField("cap");
const [budget] = defineField("budget");
const [budget_decrementer] = defineField("budget_decrementer");
const [userLimit] = defineField("userLimit");
const [deliveryLimit] = defineField("deliveryLimit");

// defineField's own model-update validation discards its result, so re-run the
// form-level validate() on value changes — it returns the errors record that
// drives validationState. Queued on a chain: each run sees the latest values
// and the last one wins; `revalidate()` lets callers await it deterministically.
let validationChain = Promise.resolve();
const revalidate = () => {
  validationChain = validationChain.then(async () => {
    const { valid, errors: resultErrors } = await validate();
    validationState.value = { valid, errors: resultErrors };
  });
  return validationChain;
};
watch(
  () => [
    values.limit,
    values.cap,
    values.budget,
    values.budget_decrementer,
    values.userLimit,
    values.deliveryLimit,
  ],
  revalidate,
);

const parseSendLimits = () => {
  const limits = {
    user_prompt: { days: null, max: null },
    user_app: { days: null, max: null },
    total_prompt: { days: null, max: null },
    total_app: { days: null, max: null },
  };
  for (const rule of props.model?.filter?.send_limits || []) {
    const days = rule.minutes ? Math.round(rule.minutes / 1440) : null;
    const max = rule.max || null;
    const key = `${rule.per}_${rule.scope}`;
    limits[key] = { days, max };
  }
  return limits;
};

// The form is a snapshot taken when the dialog opens — the model is only read
// here, never at mount (the page renders before prompt.filter resolves).
const syncFromModel = () => {
  const filter = props.model?.filter || {};

  validationState.value = { valid: true, errors: {} };
  is_limited.value = filter.traffic?.is_limited ?? false;
  trafficResetDays.value = filter.traffic?.reset_days ?? null;
  is_budgeted.value = filter.supply?.is_budgeted ?? false;
  is_capped.value = filter.frequency_cap?.is_capped ?? false;
  cap_seconds.value = filter.frequency_cap?.cap_seconds ?? null;
  hasUserLimit.value = filter.users?.is_limited ?? false;
  userResetDays.value = filter.users?.reset_days ?? null;
  hasDeliveryLimit.value = filter.deliveries?.is_limited ?? false;
  deliveryResetDays.value = filter.deliveries?.reset_days ?? null;
  frequencyCapType.value =
    filter.frequency_cap?.cap_seconds === VISIT.value ? VISIT.value : DAYS.value;
  sendLimits.value = parseSendLimits();

  resetForm({
    values: {
      limit: filter.traffic?.limit ?? 0,
      cap: filter.frequency_cap?.cap ?? 0,
      budget: filter.supply?.budget ?? 0,
      budget_decrementer: filter.supply?.budget_decrementer ?? 0,
      userLimit: filter.users?.limit ?? 0,
      deliveryLimit: filter.deliveries?.limit ?? 0,
    },
  });

  // cap_seconds must sit inside the selectable range.
  const isCapSelectExists = capSecondsInDaysItems.value.find(
    ({ value }) => value === cap_seconds.value,
  );
  if (!isCapSelectExists && capSecondsInDaysItems.value.length) {
    cap_seconds.value = capSecondsInDaysItems.value.at(-1).value;
  }
};

const toggleLimited = value => {
  is_limited.value = value;
  limit.value = value ? 1 : 0;
  if (!value) trafficResetDays.value = null;
};

const toggleBudgeted = value => {
  is_budgeted.value = value;
  budget.value = value ? 1 : 0;
};

const toggleUserLimit = value => {
  hasUserLimit.value = value;
  userLimit.value = value ? 1 : 0;
  if (!value) userResetDays.value = null;
};

const toggleDeliveryLimit = value => {
  hasDeliveryLimit.value = value;
  deliveryLimit.value = value ? 1 : 0;
  if (!value) deliveryResetDays.value = null;
};

const setFrequencyCapType = newValue => {
  frequencyCapType.value = newValue;
  if (newValue === VISIT.value) {
    cap_seconds.value = VISIT.value;
  } else if (cap_seconds.value === VISIT.value) {
    cap_seconds.value = 2592000; // 30 days in seconds - the default value
  }
};

const calculatePercent = (limitValue, current) => {
  if (limitValue <= 0 || current <= 0) return 0;
  return parseInt((current / limitValue) * 100);
};

const open = () => dialog.value?.open();
const close = () => dialog.value?.close();

const handleConfirm = async () => {
  // Let any queued validation land first, then gate on the fresh result.
  await revalidate();
  if (!validationState.value.valid) {
    // Field errors are rendered inline and the confirm button is disabled with
    // a tooltip; keep the dialog open as a guard.
    return;
  }
  const next = cloneDeep(props.model);
  fillModel(next);
  emit("confirm", next);
  close();
};

const fillModel = model => {
  model.filter.traffic.limit = parseInt(limit.value);
  model.filter.traffic.is_limited = is_limited.value;
  model.filter.traffic.reset_days = trafficResetDays.value || null;
  model.filter.supply.budget = parseInt(budget.value);
  model.filter.supply.budget_decrementer = parseInt(budget_decrementer.value);
  model.filter.supply.is_budgeted = is_budgeted.value;
  model.filter.frequency_cap.cap = parseInt(cap.value);
  model.filter.frequency_cap.cap_seconds = cap_seconds.value;
  model.filter.frequency_cap.is_capped = is_capped.value;
  model.filter.users.is_limited = hasUserLimit.value;
  model.filter.users.limit = parseInt(userLimit.value);
  model.filter.users.reset_days = userResetDays.value || null;
  model.filter.deliveries.is_limited = hasDeliveryLimit.value;
  model.filter.deliveries.limit = parseInt(deliveryLimit.value);
  model.filter.deliveries.reset_days = deliveryResetDays.value || null;
  if (isPushNotification.value) {
    model.filter.send_limits = Object.entries(sendLimits.value).reduce((arr, [key, rule]) => {
      const days = parseInt(rule.days);
      const max = parseInt(rule.max);
      if (days && max) {
        const [per, scope] = key.split("_");
        arr.push({
          per,
          scope,
          minutes: days * 1440,
          max,
        });
      }
      return arr;
    }, []);
  }
};

defineExpose({ open, close });
</script>
